import React, {useEffect, useReducer, useRef} from 'react';

import _ from 'lodash';
import I18n, {_m} from 'INTL';
import styles, {ARROW_WIDTH, CELL_HEIGHT} from './PlayerLevelExpandedCell.style';

import {
  Animated,
  Easing,
  StyleSheet,
  Text,
  View,
} from 'react-native';

export const TEST_IDS = {
  PLAYER_LEVEL_EXPANDED_CELL:'player_level_expanded_cell',
  PLAYER_LEVEL_EXPANDED_CELL_TOP_LABEL:'player_level_expanded_cell_top_label',
  PLAYER_LEVEL_EXPANDED_CELL_EXP_BAR:'player_level_expanded_cell_exp_bar',
  PLAYER_LEVEL_EXPANDED_CELL_BOTTOM_LABEL:'player_level_expanded_cell_bottom_label',
};

const NOT_FOUND_INDEX = -1;
const MAX_PERCENT = 100;

const OPEN_ANIMATION_LENGTH = 500;
const CLOSE_ANIMATION_LENGTH = 300;
const ARROW_ANIMATION_LENGTH = 600;
const ARROW_FADE_DURATION = 300;
const ARROW_FADE_DELAY = 400;
const PROGRESS_BAR_ANIMATION_LENGTH = 800;

type Level={
  /** The current level */
  currentLevel:number,
  /** How much exp has been achieved in this level */
  achieved:number,
  /** The total of exp needed to complet this level */
  total:number
};

export interface IPlayerLevelExpandedCellProps{
  /** TestID for test purpose ('player-level-expanded-cell' by default) */
  testID?:string;
  /** Allow to expand or collapse this Cell, used for animation as well. (true by default) */
  expanded?:boolean,
  /** The index of the above cell to point to (this needs numberColumns to work with) */
  arrowIndex:number,
  /** The number of columns, this is to calculate the position of the arrow with arrowIndex */
  numberColumns:number,
  /** The information about the current level */
  level?:Level|'MAX',
  /** The total experience for this level summed with all previous level */
  totalExp?:number
}

// eslint-disable-next-line no-shadow
enum EActionType {
  EXPAND=1,
  COLLAPSE,
  UPDATE_ARROW_INDEX,
}

type State={
    fillPercent: Animated.Value;
    contentOpacity: Animated.Value;
    cellHeight: Animated.Value;
    arrowPosition: Animated.Value;
    arrowOpacity: Animated.Value;
}

type Action={
    type:EActionType;
    payload?:{
        arrowIndex:number;
        cellWidth:number;
        numberColumns:number;
    }|{
        level?:Level|'MAX'
    }
}

const getTargetArrowPosition = (arrowIndex: number, cellWidth:number, numberColumn:number): number => {
  const itemWidth = cellWidth / numberColumn;
  return itemWidth * arrowIndex + itemWidth / 2 - ARROW_WIDTH / 2;
};

const reducer = (state:State, action:Action) => {
  const moveArrowAnimation = (nextArrowIndex: number) => {
    const cellWidth = (action.payload && 'cellWidth' in action.payload) ? action.payload.cellWidth : 0;
    const numberColumn = (action.payload && 'numberColumns' in action.payload) ? action.payload.numberColumns : 1;
    return Animated.timing(state.arrowPosition, {
      toValue: getTargetArrowPosition(nextArrowIndex, cellWidth, numberColumn ),
      duration: ARROW_ANIMATION_LENGTH,
      easing: Easing.inOut(Easing.quad),
      useNativeDriver: false,
    });
  };

  const fadeInContentAnimation = () => {
    return Animated.timing(state.contentOpacity, {
      toValue: 1,
      duration: OPEN_ANIMATION_LENGTH,
      useNativeDriver: false,
    });
  };

  const fadeOutContentAnimation = () => {
    return Animated.timing(state.contentOpacity, {
      toValue: 0,
      duration: CLOSE_ANIMATION_LENGTH,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: false,
    });
  };

  const fadeInArrow = () => {
    return Animated.timing(
      state.arrowOpacity, {
        toValue: 1,
        duration: ARROW_FADE_DURATION,
        delay: ARROW_FADE_DELAY,
        easing: Easing.inOut(Easing.quad),
        useNativeDriver: false,
      }
    );
  };

  const fadeOutArrow = () => {
    return Animated.timing(
      state.arrowOpacity, {
        toValue: 0,
        duration: ARROW_FADE_DURATION,
        easing: Easing.inOut(Easing.quad),
        useNativeDriver: false,
      }
    );
  };

  const expandCellAnimation = () => {
    return Animated.timing(state.cellHeight, {
      toValue: CELL_HEIGHT,
      duration: OPEN_ANIMATION_LENGTH,
      easing: Easing.inOut(Easing.quad),
      useNativeDriver: false,
    });
  };

  const collapseCellAnimation = () => {
    return Animated.timing(state.cellHeight, {
      toValue: 0,
      duration: CLOSE_ANIMATION_LENGTH,
      useNativeDriver: false,
    });
  };

  const fillProgressBarAnimation = () => {
    const experiencePercent = (action.payload && 'level' in action.payload && action.payload.level && action.payload.level !== 'MAX') ? 
      action.payload.level.achieved * MAX_PERCENT / action.payload.level.total 
      : MAX_PERCENT;

    return Animated.timing(state.fillPercent, {
      toValue: experiencePercent,
      duration: PROGRESS_BAR_ANIMATION_LENGTH,
      easing: Easing.inOut(Easing.quad),
      useNativeDriver: false,
    });
  };
  
  switch (action.type) {
    case EActionType.EXPAND:
      return {...state,
        animation:Animated.sequence([
          Animated.parallel([
            fadeInContentAnimation(),
            expandCellAnimation(),
            fadeInArrow(),
          ]),
          fillProgressBarAnimation(),
        ]).start(),
      };
    case EActionType.COLLAPSE:
      return {
        ...state,
        animation:Animated.parallel([
          fadeOutContentAnimation(),
          collapseCellAnimation(),
          fadeOutArrow(),
        ]).start(() => state.fillPercent.setValue(0)),
      };
    case EActionType.UPDATE_ARROW_INDEX:
      return {
        ...state,
        animation:
          moveArrowAnimation((action.payload && 'arrowIndex' in action.payload) ? 
            action.payload.arrowIndex :
            NOT_FOUND_INDEX).start(),
      };
      /* istanbul ignore next */ //ignore test coverage on this return as we never call dispatch with other type
    default:
      return state;
  }
};

const initState = ():State => {
  return {
    fillPercent : new Animated.Value(0),
    contentOpacity : new Animated.Value(0),
    cellHeight : new Animated.Value(0),
    arrowPosition : new Animated.Value(0),
    arrowOpacity : new Animated.Value(0),
  };
};

const getExpandedNextLevelExpText = ({level}:Pick<IPlayerLevelExpandedCellProps, 'level'>):string => {
  if (!level) {
    return '';
  }
  
  if (level === 'MAX') {
    return _m('Max Level');
  }

  const nextExp = I18n.getIntegerString(level.total - level.achieved);
  const nextLevel = level.currentLevel + 1;
  return _m('{0} XP to Level {1}', nextExp, nextLevel);
};

const getProgressionBarAccessibilityLabel = ({level}:Pick<IPlayerLevelExpandedCellProps, 'level'>):string => {
  if (!level) {
    return '';
  }
  const percent = level === 'MAX' ?
    MAX_PERCENT
    :
    Math.floor(level.achieved * MAX_PERCENT / level.total);
  return _m('{0}% achieved', percent);
};

const getTotalExpText = ({totalExp}:Pick<IPlayerLevelExpandedCellProps, 'totalExp'>):string => {
  if (!totalExp) {
    return '';
  }
  return _m('{0} Total XP', totalExp);
};

const getAccessibilityLabel = ({expanded, level, totalExp}:Pick<IPlayerLevelExpandedCellProps, 'expanded'|'level'|'totalExp'>) => {
  if (!expanded) {
    return '';
  }
  const topLabel = getExpandedNextLevelExpText({level});
  const middleLabel = getProgressionBarAccessibilityLabel({level});
  const bottomLabel = getTotalExpText({totalExp});
  if (topLabel.length > 0 && middleLabel.length > 0 && bottomLabel.length > 0) {
    return _m('{0}, {1} and {2}',
      topLabel,
      middleLabel,
      bottomLabel);
  } else if (topLabel.length > 0 && middleLabel.length > 0) {
    return _m('{0} and {1}', topLabel, middleLabel);
  } else if (middleLabel.length > 0 && bottomLabel.length > 0) {
    return _m('{0} and {1}', middleLabel, bottomLabel);
  } else if (topLabel.length > 0 && bottomLabel.length > 0) {
    return _m('{0} and {1}', topLabel, bottomLabel);
  } else if (topLabel.length > 0 ) {
    return topLabel;
  } else if (middleLabel.length > 0 ) {
    return middleLabel;
  } else if (bottomLabel.length > 0 ) {
    return bottomLabel;
  }
  return '';
};

const PlayerLevelExpandedCell = ({testID, expanded = true, arrowIndex = NOT_FOUND_INDEX, numberColumns, level, totalExp}:IPlayerLevelExpandedCellProps) => {
  const cellWidth = useRef(0);
  const [state, dispatch] = useReducer(reducer, {}, initState);
    
  useEffect(() => {
    if (expanded) {
      dispatch({type:EActionType.EXPAND, payload:{level}});
    } else {
      dispatch({type:EActionType.COLLAPSE});
    }
  }, [expanded]);

  useEffect(() => {
    dispatch({type:EActionType.UPDATE_ARROW_INDEX, payload:{arrowIndex, cellWidth:cellWidth.current, numberColumns}});
  }, [arrowIndex, numberColumns]);

  useEffect(() => {
    if (!level) {
      dispatch({type:EActionType.COLLAPSE});
    } else if (expanded) {
      dispatch({type:EActionType.EXPAND, payload:{level}});
    }
  }, [level]);

  const renderArrow = () => {
    return (
      <Animated.View style={[{
        position: 'absolute', top: 0, bottom: 0, left: state.arrowPosition,
        opacity: state.arrowOpacity, 
        width: ARROW_WIDTH,
      }]} 
                     key={'arrow'}>
        <View style={styles.expandedArrow}/>
      </Animated.View>
    );
  };

  const renderExpandedArrow = () => {
    const arrowCell = renderArrow();
    return (
      <View style={styles.expandedArrowContainer}>
        {arrowIndex > NOT_FOUND_INDEX && arrowCell}
      </View>
    );
  };

  const renderTopLabel = () => {
    if (!level) {
      return null;
    }
    return (
      <Text testID={TEST_IDS.PLAYER_LEVEL_EXPANDED_CELL_TOP_LABEL} 
            style={styles.nextExpText}>
        {getExpandedNextLevelExpText({level})}
      </Text>
    );
  };

  const renderProgressBar = () => {
    if (!level || level === 'MAX') {
      return null;
    }
    const percent = state.fillPercent.interpolate({
      inputRange: [0, MAX_PERCENT],
      outputRange: ['0%', '100%'],
    });
    return (
      <View testID={TEST_IDS.PLAYER_LEVEL_EXPANDED_CELL_EXP_BAR}
            style={[styles.progressBarContainer]}>
        <View style={StyleSheet.absoluteFill}/>
        <Animated.View style={[styles.progressBarFill, {width: percent}]}/>
      </View>
    );
  };

  const renderBottomLabel = () => {
    if (!totalExp) {
      return null;
    }
    return (
      <Text testID={TEST_IDS.PLAYER_LEVEL_EXPANDED_CELL_BOTTOM_LABEL}
            style={styles.totalExpText}>
        {getTotalExpText({totalExp})}
      </Text>
    );
  };

  return (
    <View testID={testID || TEST_IDS.PLAYER_LEVEL_EXPANDED_CELL} 
          style={styles.expandedCellContainer}
          onLayout={(event) => {
            cellWidth.current = event.nativeEvent.layout.width;
            dispatch({type:EActionType.UPDATE_ARROW_INDEX, payload:{arrowIndex, cellWidth:event.nativeEvent.layout.width, numberColumns}}); 
          }}
          accessibilityLabel={getAccessibilityLabel({expanded, level, totalExp})}
          accessible={true}>
      {renderExpandedArrow()}
      <Animated.View style={[styles.expandedCell, {height: state.cellHeight}]}>
        <Animated.View style={[styles.expandedCellContent, {opacity: state.contentOpacity}]} >
          {renderTopLabel()}
          {renderProgressBar()}
          {renderBottomLabel()}
        </Animated.View>
      </Animated.View>
    </View>
  );
};

export default PlayerLevelExpandedCell;
